import React from 'react'
import {
  DcButton,
  DcFlexBox,
  DcInput,
  DcText,
  PixelFactoryContext,
  Section,
  SectionContent,
  colors,
} from 'pixel-base'
import type { AjaxError, AjaxResponse } from 'rxjs/ajax'
import styled from 'styled-components'

import {
  createProductBom,
  type CreateBomComponent,
  type BulkUploadResponse,
} from 'utils/api-routes-planning'
import { PlusIcon } from 'utils/icons/plus-icon'
import * as XLSX from 'xlsx'
import { IPixelFactory } from '../../../../../decl.d'

interface BomAddFormProps {
  productId: number | string
  productSku: string
  onSuccess: () => void
}

const DropZone = styled.label<{ $isDragging: boolean }>`
  width: 100%;
  border: 1px dashed
    ${({ $isDragging }) => ($isDragging ? colors.primary : '#dcdfe6')};
  border-radius: 12px;
  padding: 20px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  gap: 8px;
  cursor: pointer;
  background-color: ${({ $isDragging }) =>
    $isDragging ? 'rgba(92,77,255,0.05)' : 'transparent'};
  transition: all 0.2s ease-in-out;
`

const ACCEPTED_MIME_TYPES = [
  'text/csv',
  'application/vnd.ms-excel',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
]

type SheetRow = Record<
  string,
  string | number | boolean | Date | null | undefined
>

const BomAddForm: React.FC<BomAddFormProps> = ({
  productId,
  productSku,
  onSuccess,
}) => {
  const { showFailure, showSuccess, showPageLoader, hidePageLoader } =
    React.useContext<IPixelFactory>(PixelFactoryContext)
  const [components, setComponents] = React.useState<CreateBomComponent[]>([
    {
      component_sku: '',
      quantity_required: 0,
      notes: '',
    },
  ])
  const [isSubmitting, setIsSubmitting] = React.useState(false)
  const [error, setError] = React.useState<string | null>(null)
  const [fileName, setFileName] = React.useState('')
  const [isDragging, setIsDragging] = React.useState(false)
  const fileInputRef = React.useRef<HTMLInputElement | null>(null)

  const normalizeKey = (value: string) => {
    if (!value) return ''
    return value.replace(/[\s_-]/g, '').toLowerCase()
  }

  const extractValue = (row: SheetRow, matcherKeys: string[]): string => {
    const normalizedMatchers = matcherKeys.map((key) => normalizeKey(key))

    const matchedKey = Object.keys(row).find((key) =>
      normalizedMatchers.includes(normalizeKey(key)),
    )

    if (!matchedKey) {
      return ''
    }

    const value = row[matchedKey]
    if (value === undefined || value === null) {
      return ''
    }

    if (typeof value === 'number') {
      return value.toString()
    }

    return value.toString().trim()
  }

  const extractNumber = (
    row: SheetRow,
    matcherKeys: string[],
  ): number | undefined => {
    const normalizedMatchers = matcherKeys.map((key) => normalizeKey(key))

    const matchedKey = Object.keys(row).find((key) =>
      normalizedMatchers.includes(normalizeKey(key)),
    )

    if (!matchedKey) {
      return undefined
    }

    const value = row[matchedKey]

    if (value === undefined || value === null || value === '') {
      return undefined
    }

    if (typeof value === 'number' && Number.isFinite(value)) {
      return value
    }

    const parsedValue = Number(value)
    if (Number.isFinite(parsedValue)) {
      return parsedValue
    }

    return undefined
  }

  const mapRowsToComponents = (rows: SheetRow[]): CreateBomComponent[] => {
    return rows
      .map((row) => {
        const sku = extractValue(row, [
          'component_sku',
          'componentsku',
          'sku',
          'product_sku',
        ])

        const qty =
          extractNumber(row, [
            'quantity_required',
            'qty_required',
            'required_qty',
            'qty',
            'quantity',
          ]) ?? 0

        const notes = extractValue(row, ['notes', 'note', 'description'])

        return {
          component_sku: sku,
          quantity_required: qty,
          notes,
        }
      })
      .filter(
        (item) =>
          item.component_sku.trim() !== '' && item.quantity_required > 0,
      )
  }

  const processFile = (file: File) => {
    const isAccepted =
      ACCEPTED_MIME_TYPES.includes(file.type) ||
      file.name.endsWith('.csv') ||
      file.name.endsWith('.xls') ||
      file.name.endsWith('.xlsx')

    if (!isAccepted) {
      const message = 'Please upload a CSV or Excel file (.csv, .xls, .xlsx).'
      setError(message)
      showFailure(message)
      return
    }

    const reader = new FileReader()
    reader.onload = (event: ProgressEvent<FileReader>) => {
      try {
        const buffer = event.target?.result
        if (!buffer) {
          throw new Error('Unable to read file')
        }

        const data = new Uint8Array(buffer as ArrayBuffer)
        const workbook = XLSX.read(data, { type: 'array' })
        const firstSheetName = workbook.SheetNames[0]
        const worksheet = workbook.Sheets[firstSheetName]
        const jsonData = XLSX.utils.sheet_to_json<SheetRow>(worksheet, {
          defval: '',
        })

        if (jsonData.length === 0) {
          const message = 'No data found in the uploaded file.'
          setError(message)
          showFailure(message)
          return
        }

        const parsedComponents = mapRowsToComponents(jsonData)

        if (parsedComponents.length === 0) {
          const message =
            'No valid components found. Ensure the file has Component SKU and Quantity columns.'
          setError(message)
          showFailure(message)
          return
        }

        setComponents(parsedComponents)
        setError(null)
        showSuccess(
          `Parsed ${parsedComponents.length} component(s) from the sheet.`,
        )
      } catch (err) {
        console.error('bom-upload:parse-error', err)
        const message = 'Failed to parse the uploaded file. Please try again.'
        setError(message)
        showFailure(message)
      }
    }

    reader.readAsArrayBuffer(file)
  }

  const handleFileSelect = (
    event: React.ChangeEvent<HTMLInputElement>,
  ): void => {
    const file = event.target.files?.[0]
    if (!file) return

    setFileName(file.name)
    processFile(file)
  }

  const handleDrop = (event: React.DragEvent<HTMLLabelElement>) => {
    event.preventDefault()
    event.stopPropagation()
    setIsDragging(false)

    const file = event.dataTransfer.files?.[0]
    if (!file) return

    setFileName(file.name)
    processFile(file)
  }

  const handleDragOver = (event: React.DragEvent<HTMLLabelElement>) => {
    event.preventDefault()
    event.stopPropagation()
    setIsDragging(true)
  }

  const handleDragLeave = (event: React.DragEvent<HTMLLabelElement>) => {
    event.preventDefault()
    event.stopPropagation()
    setIsDragging(false)
  }

  const handleDownloadTemplate = () => {
    const templateRows = [
      {
        component_sku: '',
        quantity_required: '',
        notes: 'Optional notes',
      },
      {
        component_sku: '',
        quantity_required: '',
        notes: '',
      },
    ]

    const worksheet = XLSX.utils.json_to_sheet(templateRows)
    const workbook = XLSX.utils.book_new()
    XLSX.utils.book_append_sheet(workbook, worksheet, 'BOM')
    //name shouldbe the product sku with _bom.xlsx
    XLSX.writeFile(workbook, `${productSku}_bom.xlsx`)
  }

  const handleAddRow = () => {
    setComponents([
      ...components,
      {
        component_sku: '',
        quantity_required: 0,
        notes: '',
      },
    ])
  }

  const handleRemoveRow = (index: number) => {
    if (components.length > 1) {
      setComponents(components.filter((_, i) => i !== index))
    }
  }

  const handleComponentChange = (
    index: number,
    field: keyof CreateBomComponent,
    value: string | number,
  ) => {
    const updated = [...components]
    updated[index] = {
      ...updated[index],
      [field]: value,
    }
    setComponents(updated)
  }

  const handleSubmit = () => {
    // Validate all components
    const validComponents = components.filter(
      (comp) => comp.component_sku.trim() !== '' && comp.quantity_required > 0,
    )

    if (validComponents.length === 0) {
      setError('Please add at least one component with SKU and quantity')
      return
    }

    setError(null)
    setIsSubmitting(true)

    const payload = {
      components: validComponents.map((comp) => ({
        component_sku: comp.component_sku.trim(),
        quantity_required: comp.quantity_required,
        ...(comp.notes?.trim() && { notes: comp.notes.trim() }),
      })),
    }

    createProductBom(productId, payload).subscribe({
      next: (_res: AjaxResponse<BulkUploadResponse>) => {
        setIsSubmitting(false)
        // Reset form
        setComponents([
          {
            component_sku: '',
            quantity_required: 0,
            notes: '',
          },
        ])
        onSuccess()
      },
      error: (err: AjaxError) => {
        console.error('bom-create:error', err)
        setError(
          err.response?.error ||
            'Failed to create BOM components. Please try again.',
        )
        setIsSubmitting(false)
      },
    })
  }

  return (
    <Section style={{ padding: '20px' }}>
      <SectionContent>
        <DcFlexBox flexDirection="column" gap="16px">
          <DcText style={{ fontSize: '16px', fontWeight: 600 }}>
            Add BOM Components
          </DcText>

          <DcFlexBox flexDirection="column" gap="8px">
            <DcText style={{ fontSize: '13px', color: '#6b7280' }}>
              Download the&nbsp;
              <a
                style={{ color: colors.primary, cursor: 'pointer' }}
                onClick={handleDownloadTemplate}
                target="_blank"
              >
                template
              </a>
              , add your BOM components with the required columns, and upload
              the sheet below.
            </DcText>
            <DropZone
              $isDragging={isDragging}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
              htmlFor="bom-components-upload"
            >
              <input
                id="bom-components-upload"
                type="file"
                accept=".csv,.xls,.xlsx"
                style={{ display: 'none' }}
                ref={fileInputRef}
                onChange={handleFileSelect}
              />
              <DcText style={{ fontSize: '13px', color: '#374151' }}>
                {fileName
                  ? `Selected file: ${fileName}`
                  : 'Click to browse or drag & drop a sheet here'}
              </DcText>
              <DcText style={{ fontSize: '12px', color: '#6b7280' }}>
                Supported formats: .csv, .xls, .xlsx
              </DcText>
            </DropZone>
          </DcFlexBox>

          {error && (
            <DcText style={{ fontSize: '13px', color: '#b91c1c' }}>
              {error}
            </DcText>
          )}

          <DcFlexBox flexDirection="column" gap="12px">
            {components.map((component, index) => (
              <DcFlexBox
                key={index}
                gap="12px"
                alignItems="flex-end"
                style={{
                  padding: '12px',
                  border: '1px solid #e5e7eb',
                  borderRadius: '8px',
                }}
              >
                <DcFlexBox flexDirection="column" gap="4px" style={{ flex: 1 }}>
                  <DcText style={{ fontSize: '12px', color: '#6b7280' }}>
                    Component SKU *
                  </DcText>
                  <DcInput
                    type="text"
                    value={component.component_sku}
                    onChange={(e) =>
                      handleComponentChange(
                        index,
                        'component_sku',
                        e.target.value,
                      )
                    }
                    placeholder="Enter component SKU"
                    style={{ fontSize: '13px' }}
                  />
                </DcFlexBox>

                <DcFlexBox
                  flexDirection="column"
                  gap="4px"
                  style={{ width: '120px' }}
                >
                  <DcText style={{ fontSize: '12px', color: '#6b7280' }}>
                    Quantity *
                  </DcText>
                  <DcInput
                    type="number"
                    min={0}
                    step="0.01"
                    value={component.quantity_required}
                    onChange={(e) =>
                      handleComponentChange(
                        index,
                        'quantity_required',
                        Number(e.target.value) || 0,
                      )
                    }
                    placeholder="0"
                    style={{ fontSize: '13px' }}
                  />
                </DcFlexBox>

                <DcFlexBox flexDirection="column" gap="4px" style={{ flex: 1 }}>
                  <DcText style={{ fontSize: '12px', color: '#6b7280' }}>
                    Notes (Optional)
                  </DcText>
                  <DcInput
                    type="text"
                    value={component.notes || ''}
                    onChange={(e) =>
                      handleComponentChange(index, 'notes', e.target.value)
                    }
                    placeholder="Optional notes"
                    style={{ fontSize: '13px' }}
                  />
                </DcFlexBox>

                {components.length > 1 && (
                  <DcButton
                    variant="outline-secondary"
                    size="sm"
                    onClick={() => handleRemoveRow(index)}
                    style={{ marginBottom: '4px' }}
                  >
                    Remove
                  </DcButton>
                )}
              </DcFlexBox>
            ))}
          </DcFlexBox>

          <DcFlexBox gap="12px" justifyContent="space-between">
            <DcButton
              variant="outline-secondary"
              size="sm"
              onClick={handleAddRow}
              withIcon={true}
            >
              <PlusIcon color="currentColor" size={16} />
              <span>Add Another Component</span>
            </DcButton>

            <DcButton
              variant="primary"
              size="sm"
              onClick={handleSubmit}
              disabled={isSubmitting}
            >
              {isSubmitting ? 'Adding...' : 'Add Components'}
            </DcButton>
          </DcFlexBox>
        </DcFlexBox>
      </SectionContent>
    </Section>
  )
}

export default BomAddForm
